import { useEffect, useState } from "react";
import api from "../../lib/api";
import { apiError, formatAmount, formatDay } from "../../lib/finance";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const payerName = (f) => (f.payer_type === "company" ? "Jamora (l'entreprise)" : f.payer_label);

// Cancelling a payment of a submitted expense: nothing is erased, the payment stays visible marked « Annulé »
// with its reason and stops counting; the right payment is then recorded as a new one.
export default function CancelPaymentDialog({ expense, funding, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setReason(""); setShowError(false); }, [funding?.id]);

  const confirm = async () => {
    if (!reason.trim()) { setShowError(true); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/finance/expenses/${expense.id}/payments/${funding.id}/cancel`,
        { expected_version: expense.version, reason: reason.trim() });
      toast.success("Paiement annulé");
      onCancelled(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={!!funding} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <AlertDialogContent data-testid="finance-cancel-payment-dialog">
        {funding && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler ce paiement ?</AlertDialogTitle>
              <AlertDialogDescription>
                {payerName(funding)}, {formatAmount(funding.amount, expense.currency)}{funding.paid_at ? `, payé le ${formatDay(funding.paid_at)}` : ""}.
                Il restera visible, marqué « Annulé », mais ne comptera plus. Vous pourrez ensuite enregistrer le bon paiement.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="fin-cancel-reason">Motif de l'annulation *</Label>
              <Textarea id="fin-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
                placeholder="Ex. : mauvaise date, mauvais payeur, paiement saisi deux fois" className="text-base md:text-sm"
                aria-invalid={showError && !reason.trim()} data-testid="finance-cancel-reason" />
              {showError && !reason.trim() && <p role="alert" className="text-xs text-red-700">Indiquez le motif de l'annulation.</p>}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Retour</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid="finance-cancel-payment-confirm">
                {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Annuler le paiement
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
